import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type GallerySource = 'posts' | 'reposts' | 'replies' | 'likes'
export type Sensitivity = 'all' | 'safe' | 'adult'
export type GalleryKind = 'txt' | 'img' | 'vid'

const KIND_ORDER: GalleryKind[] = ['txt', 'img', 'vid']

interface State {
  enabledSources: GallerySource[]
  sensitivity: Sensitivity
  kinds: GalleryKind[]
  columns: number
  fsColumns: number
  /** Group gallery tiles under per-user headers instead of flat grid */
  groupByUser: boolean
  /** Within each user group, add sub-headers per source (posts/reposts/…) */
  groupBySource: boolean
  /** Session-only: filter gallery/list to only deleted (⊘-flagged) items */
  showDeletedOnly: boolean
  /** When false (default), clicking a search result does NOT auto-add the
   *  user to the gallery — only the explicit "+ gallery" button adds. */
  autoAddFromSearch: boolean
  toggleSource: (s: GallerySource) => void
  setSensitivity: (s: Sensitivity) => void
  toggleKind: (k: GalleryKind) => void
  setColumns: (n: number) => void
  setFsColumns: (n: number) => void
  toggleGroupByUser: () => void
  toggleGroupBySource: () => void
  toggleShowDeletedOnly: () => void
  toggleAutoAddFromSearch: () => void
}

export const useGalleryPrefs = create<State>()(
  persist(
    (set, get) => ({
      enabledSources: ['posts'],
      sensitivity: 'all',
      kinds: ['txt', 'img', 'vid'],
      columns: 4,
      fsColumns: 8,
      groupByUser: false,
      groupBySource: false,
      showDeletedOnly: false,
      autoAddFromSearch: false,
      toggleSource: (s) =>
        set(() => {
          const cur = new Set(get().enabledSources)
          if (cur.has(s)) cur.delete(s)
          else cur.add(s)
          if (cur.size === 0) cur.add(s)
          const order: GallerySource[] = ['posts', 'replies', 'reposts', 'likes']
          return { enabledSources: order.filter((x) => cur.has(x)) }
        }),
      setSensitivity: (s) => set({ sensitivity: s }),
      toggleKind: (k) =>
        set(() => {
          const cur = new Set(get().kinds)
          if (cur.has(k)) cur.delete(k)
          else cur.add(k)
          return { kinds: KIND_ORDER.filter((x) => cur.has(x)) }
        }),
      setColumns: (n) =>
        set({ columns: Math.max(2, Math.min(12, Math.round(n))) }),
      setFsColumns: (n) =>
        set({ fsColumns: Math.max(3, Math.min(20, Math.round(n))) }),
      toggleGroupByUser: () => set({ groupByUser: !get().groupByUser }),
      toggleGroupBySource: () => set({ groupBySource: !get().groupBySource }),
      toggleShowDeletedOnly: () => set({ showDeletedOnly: !get().showDeletedOnly }),
      toggleAutoAddFromSearch: () => set({ autoAddFromSearch: !get().autoAddFromSearch }),
    }),
    {
      name: 'bskyhq:gallery',
      version: 3,
      partialize: ({ toggleSource: _ts, setSensitivity: _ss,
        toggleKind: _tk, setColumns: _sc, setFsColumns: _sf, toggleGroupByUser: _tg,
        toggleGroupBySource: _tgs, toggleShowDeletedOnly: _tsd,
        toggleAutoAddFromSearch: _ta, ...rest }) => rest,
    },
  ),
)
